import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Lock, Mail, Phone, ArrowRight, RotateCcw, Loader2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const expectations = [
  { title: "5-Min Discovery", body: "We review your current call volume and the inquiries your team handles most." },
  { title: "5-Min Custom Voice Test", body: "Hear an agent trained on your business — your services, hours and pricing." },
  { title: "5-Min Deployment Plan", body: "A simple call-forwarding setup plan for your existing number. No hardware." },
];

const faqs = [
  {
    q: "Do I need to change my business phone number?",
    a: "No. CogniTask works via conditional call forwarding on your existing number — calls are answered by the AI only when your line is busy, unanswered, or after hours.",
  },
  {
    q: "Does it understand Indian accents?",
    a: "Yes. CogniTask is tuned for Indian English accents out of the box, with Hindi and additional regional language support on our near-term roadmap.",
  },
  {
    q: "What calendar tools are supported?",
    a: "Google Calendar, Outlook and Calendly are supported natively, and custom scheduling tools can be connected via webhooks.",
  },
];

const industryOptions = [
  { value: "healthcare", label: "Healthcare / Clinic" },
  { value: "retail", label: "Retail / Store" },
  { value: "salon", label: "Salon / Wellness" },
  { value: "legal", label: "Legal / Finance" },
  { value: "other", label: "Other" },
];

const volumeOptions = [
  { value: "lt250", label: "< 250 calls" },
  { value: "250-1000", label: "250 - 1,000 calls" },
  { value: "1000-3000", label: "1,000 - 3,000 calls" },
  { value: "gt3000", label: "3,000+ calls" },
];

const industryParamMap = { clinic: "healthcare", retail: "retail" };

const emptyForm = {
  fullName: "",
  email: "",
  phone: "+91 ",
  business: "",
  industry: "",
  volume: "",
  notes: "",
  consent: false,
};

export default function Contact() {
  const [searchParams] = useSearchParams();
  const preselected = industryParamMap[searchParams.get("industry")] || "";
  const [form, setForm] = useState({ ...emptyForm, industry: preselected });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = "Full name is required";
    if (!form.email.trim()) next.email = "Work email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Enter a valid email address";
    if (!form.phone.trim() || form.phone.trim() === "+91") next.phone = "Phone number is required";
    if (!form.business.trim()) next.business = "Business name is required";
    if (!form.industry) next.industry = "Select your industry";
    if (!form.volume) next.volume = "Select your monthly call volume";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      await axios.post(`${API}/leads`, {
        full_name: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        business: form.business.trim(),
        industry: form.industry,
        volume: form.volume,
        notes: form.notes.trim(),
        consent: form.consent,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError("Something went wrong while sending your request. Please try again, or email us at support@cognitask.co.in.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({ ...emptyForm });
    setErrors({});
    setSubmitError("");
    setSubmitted(false);
  };

  const inputClass = "border-slate-300 focus-visible:ring-indigo-600";
  const errorText = (key) =>
    errors[key] ? (
      <p className="mt-1.5 text-xs font-medium text-rose-600" data-testid={`error-${key}`}>{errors[key]}</p>
    ) : null;

  return (
    <main data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="grid gap-12 lg:grid-cols-5 lg:gap-16"
        >
          {/* Left: trust & expectations */}
          <div className="lg:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Get Started</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              See How CogniTask Sounds on Your Own Phone Line
            </h2>
            <div className="mt-10 space-y-7" data-testid="contact-expectations">
              {expectations.map((s, i) => (
                <div key={s.title} className="flex items-start gap-4">
                  <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-50">
                    <CheckCircle2 className="h-5 w-5 text-indigo-600" />
                    <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">
                      {i + 1}
                    </span>
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">{s.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="contact-trust-badge">
              <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <Lock className="h-4 w-4" />
                DPDP Act 2023 Compliant. Form data encrypted.
              </p>
            </div>
            <p className="mt-6 flex items-center gap-2 text-sm text-slate-600">
              <Mail className="h-4 w-4 text-indigo-600" />
              Prefer email?
              <a href="mailto:support@cognitask.co.in" data-testid="contact-email-link" className="font-semibold text-indigo-600 hover:text-indigo-700">
                support@cognitask.co.in
              </a>
            </p>
            <p className="mt-3 flex items-center gap-2 text-sm text-slate-600">
              <Phone className="h-4 w-4 text-indigo-600" />
              Call us:
              <a href="tel:+919891350540" data-testid="contact-phone-link" className="font-semibold text-indigo-600 hover:text-indigo-700">
                +91 98913 50540
              </a>
            </p>
          </div>

          {/* Right: lead form / success */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-md" data-testid="contact-success-state">
                <span className="success-pop flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                  <CheckCircle2 className="h-9 w-9 text-emerald-600" />
                </span>
                <h3 className="mt-6 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                  Demo Request Received for {form.business}!
                </h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">
                  A voice architect will contact you within 4 business hours with your custom voice preview.
                  A confirmation email has been sent to {form.email}.
                </p>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="contact-reset-button"
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-colors duration-200 hover:border-indigo-300 hover:text-indigo-700"
                >
                  <RotateCcw className="h-4 w-4" />
                  Submit another response
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="rounded-2xl border border-slate-200 bg-white p-6 shadow-md sm:p-10" data-testid="contact-lead-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="fullName" className="text-slate-700">Full Name *</Label>
                    <Input id="fullName" data-testid="input-full-name" className={`mt-1.5 ${inputClass}`} placeholder="Ananya Verma" value={form.fullName} onChange={(e) => set("fullName", e.target.value)} />
                    {errorText("fullName")}
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-700">Business Work Email *</Label>
                    <Input id="email" type="email" data-testid="input-email" className={`mt-1.5 ${inputClass}`} placeholder="ananya@yourclinic.in" value={form.email} onChange={(e) => set("email", e.target.value)} />
                    {errorText("email")}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700">Phone Number *</Label>
                    <Input id="phone" type="tel" data-testid="input-phone" className={`mt-1.5 ${inputClass}`} placeholder="+91 98765 43210" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                    {errorText("phone")}
                  </div>
                  <div>
                    <Label htmlFor="business" className="text-slate-700">Business Name *</Label>
                    <Input id="business" data-testid="input-business" className={`mt-1.5 ${inputClass}`} placeholder="BrightSmile Dental Studio" value={form.business} onChange={(e) => set("business", e.target.value)} />
                    {errorText("business")}
                  </div>
                  <div>
                    <Label className="text-slate-700">Industry Sector *</Label>
                    <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                      <SelectTrigger data-testid="select-industry" className="mt-1.5 border-slate-300 focus:ring-indigo-600">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {industryOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value} data-testid={`industry-option-${o.value}`}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errorText("industry")}
                  </div>
                  <div>
                    <Label className="text-slate-700">Monthly Call Volume *</Label>
                    <Select value={form.volume} onValueChange={(v) => set("volume", v)}>
                      <SelectTrigger data-testid="select-volume" className="mt-1.5 border-slate-300 focus:ring-indigo-600">
                        <SelectValue placeholder="Select call volume" />
                      </SelectTrigger>
                      <SelectContent>
                        {volumeOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value} data-testid={`volume-option-${o.value}`}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errorText("volume")}
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="notes" className="text-slate-700">Notes / Goals (optional)</Label>
                    <Textarea id="notes" data-testid="input-notes" className={`mt-1.5 min-h-[110px] ${inputClass}`} placeholder="Tell us about your call flow, peak hours, or what you'd like the AI to handle first…" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
                  </div>
                  <div className="flex items-start gap-3 sm:col-span-2">
                    <Checkbox id="consent" data-testid="input-consent" checked={form.consent} onCheckedChange={(v) => set("consent", v === true)} className="mt-0.5 border-slate-300 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600" />
                    <Label htmlFor="consent" className="text-sm font-normal leading-relaxed text-slate-600">
                      I agree to receive demo confirmation and product updates.
                    </Label>
                  </div>
                </div>
                {submitError && (
                  <p className="mt-5 flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700" data-testid="contact-submit-error">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {submitError}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-indigo-600 px-8 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending Your Request…
                    </>
                  ) : (
                    <>
                      Schedule My Live Voice Demo
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <p className="text-center text-xs font-semibold uppercase tracking-wider text-indigo-600">Quick Answers</p>
          <h2 className="mt-3 text-center text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} data-testid="faq-accordion-item" className="border-slate-200">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-base font-semibold text-slate-900 hover:text-indigo-700 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </main>
  );
}
